/**
 * Container Hooks - React Query hooks for Container domain
 * 
 * Uses Container.Info types from @sbclite/core for type safety
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Container } from '@sbclite/core';
import { api } from '../services/api';

/**
 * Container API Response
 */
interface ContainerListResponse {
  containers: Container.Info[];
  summary: {
    total: number;
    healthy: number;
    sipEngines: number;
  };
}

interface ContainerPrerequisitesResponse {
  readonly success: boolean;
  readonly data: Container.ContainerPrerequisiteReport;
}

export const containerQueryKeys = {
  containers: ['containers'] as const,
  container: (containerId: string) => ['container', containerId] as const,
  prerequisitesRoot: ['container-prerequisites'] as const,
  prerequisites: (containerId: string) => ['container-prerequisites', containerId] as const,
  metrics: (containerId: string) => ['container-metrics', containerId] as const,
} as const;

/**
 * Fetch all containers
 */
export function useContainers(serviceType?: 'SipEngine') {
  return useQuery({
    queryKey: [...containerQueryKeys.containers, serviceType] as const,
    queryFn: async (): Promise<ContainerListResponse> => {
      const params = new URLSearchParams();
      if (serviceType) {
        params.append('serviceType', serviceType);
      }

      const response = await api.get<ContainerListResponse>(`/api/containers?${params.toString()}`);
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch SIP Engine containers only
 */
export function useSipEngines() {
  return useContainers('SipEngine');
}

/**
 * Fetch single container by ID
 */
export function useContainer(containerId: string) {
  return useQuery<Container.Info>({
    queryKey: containerQueryKeys.container(containerId),
    queryFn: async () => {
      const response = await api.get<Container.Info>(`/api/containers/${containerId}`);
      return response.data;
    },
    enabled: !!containerId,
  });
}

export function useContainerPrerequisites(containerId: string, enabled = true) {
  return useQuery({
    queryKey: containerQueryKeys.prerequisites(containerId),
    queryFn: async (): Promise<Container.ContainerPrerequisiteReport> => {
      const response = await api.get<ContainerPrerequisitesResponse>(`/api/system/engine-prerequisites/${encodeURIComponent(containerId)}`);
      return response.data.data;
    },
    enabled: enabled && !!containerId,
    refetchInterval: 15000,
  });
}

/**
 * Container metrics
 */
export function useContainerMetrics(containerId: string) {
  return useQuery<Container.Metric.Info[]>({
    queryKey: containerQueryKeys.metrics(containerId),
    queryFn: async () => {
      const response = await api.get<Container.Metric.Info[]>(`/api/containers/${containerId}/metrics`);
      return response.data;
    },
    enabled: !!containerId,
    refetchInterval: 10000, // Refresh every 10 seconds
  });
}

/**
 * Cleanup stale containers mutation
 */
export function useCleanupContainers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/containers/cleanup');
      return response.data;
    },
    onSuccess: () => {
      // Invalidate containers query to refetch
      queryClient.invalidateQueries({ queryKey: containerQueryKeys.containers });
    },
  });
}
